import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
  IconButton, Tooltip, InputAdornment, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Grid, Snackbar, Alert
} from '@mui/material';
import {
  Search, Refresh, Edit as EditIcon, CloudUpload, CurrencyRupee
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';

const fmt = (val) => '₹' + (val || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 });

export default function AstrologersPage() {
  const [astrologers, setAstrologers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editAstro, setEditAstro] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Rates dialog
  const [ratesOpen, setRatesOpen] = useState(false);
  const [ratesAstro, setRatesAstro] = useState(null);
  const [ratesForm, setRatesForm] = useState({});
  const [savingRates, setSavingRates] = useState(false);

  // Image upload
  const [uploading, setUploading] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const fetchAstrologers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/astrologers');
      const data = res.data?.data || res.data || [];
      const list = Array.isArray(data) ? data : data.astrologers || [];
      setAstrologers(list);
    } catch (err) {
      console.error('Failed to fetch astrologers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAstrologers(); }, [fetchAstrologers]);

  const filtered = astrologers.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = (a.name || a.user?.name || '').toLowerCase();
    const email = (a.email || a.user?.email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  // --- Edit Profile ---
  const openEdit = async (astro) => {
    try {
      const res = await api.get(`/admin/astrologers/${astro.id}/profile`);
      const p = res.data;
      setEditAstro(p);
      setEditForm({
        bio: p.bio || '',
        specialization: p.specialization || '',
        expertise: p.expertise || '',
        languages: p.languages || '',
        experience_years: p.experience_years || 0,
      });
      setEditOpen(true);
    } catch (err) {
      setSnack({ open: true, message: 'Failed to load profile', severity: 'error' });
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/astrologers/${editAstro.id}/profile`, editForm);
      setSnack({ open: true, message: 'Profile updated', severity: 'success' });
      setEditOpen(false);
      fetchAstrologers();
    } catch (err) {
      setSnack({ open: true, message: 'Failed to update profile', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // --- Rates ---
  const openRates = async (astro) => {
    try {
      const res = await api.get(`/admin/astrologers/${astro.id}/rates`);
      const r = res.data;
      setRatesAstro(astro);
      setRatesForm({
        chat_rate_per_min: r.chat_rate_per_min || 0,
        voice_rate_per_min: r.voice_rate_per_min || 0,
        video_rate_per_min: r.video_rate_per_min || 0,
      });
      setRatesOpen(true);
    } catch (err) {
      setSnack({ open: true, message: 'Failed to load rates', severity: 'error' });
    }
  };

  const saveRates = async () => {
    setSavingRates(true);
    try {
      await api.put(`/admin/astrologers/${ratesAstro.id}/rates`, ratesForm);
      setSnack({ open: true, message: 'Rates updated', severity: 'success' });
      setRatesOpen(false);
      fetchAstrologers();
    } catch (err) {
      setSnack({ open: true, message: 'Failed to update rates', severity: 'error' });
    } finally {
      setSavingRates(false);
    }
  };

  // --- Image Upload ---
  const handleImageUpload = async (astro, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      await api.post(`/admin/astrologers/${astro.id}/profile-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSnack({ open: true, message: 'Image uploaded', severity: 'success' });
      fetchAstrologers();
    } catch (err) {
      setSnack({ open: true, message: 'Failed to upload image', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminLayout title="Astrologers">
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5">Astrologer Management</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
              }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchAstrologers}><Refresh /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>ID</TableCell>
                <TableCell>Photo</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>Expertise</TableCell>
                <TableCell align="center">Exp</TableCell>
                <TableCell align="right">Chat</TableCell>
                <TableCell align="right">Voice</TableCell>
                <TableCell align="right">Video</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Consults</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No astrologers found.
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.map((a) => {
                const name = a.name || a.user?.name || '-';
                const img = a.profile_image_url || a.user?.profile_image || '';
                return (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.id}</TableCell>
                    <TableCell>
                      <Avatar src={img} alt={name} sx={{ width: 36, height: 36 }}>
                        {name.charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {a.email || a.user?.email || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{a.specialization || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      {(a.expertise || '').split(',').filter(Boolean).map((e, i) => (
                        <Chip key={i} label={e.trim()} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </TableCell>
                    <TableCell align="center">{a.experience_years || 0}y</TableCell>
                    <TableCell align="right">{fmt(a.price_per_minute_chat)}</TableCell>
                    <TableCell align="right">{fmt(a.price_per_minute_call)}</TableCell>
                    <TableCell align="right">{fmt(a.price_per_minute_video)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={a.online_status || (a.is_online ? 'online' : 'offline')}
                        size="small"
                        color={a.is_online ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">{a.total_consultations || 0}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit Profile">
                          <IconButton size="small" onClick={() => openEdit(a)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Set Rates">
                          <IconButton size="small" onClick={() => openRates(a)}>
                            <CurrencyRupee fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Upload Photo">
                          <IconButton size="small" component="label" disabled={uploading}>
                            <CloudUpload fontSize="small" />
                            <input
                              type="file"
                              hidden
                              accept="image/jpeg,image/png,image/webp"
                              onChange={(e) => handleImageUpload(a, e.target.files[0])}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Edit Profile Dialog */}
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Astrologer Profile</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  label="Bio / About Me"
                  fullWidth multiline rows={3}
                  value={editForm.bio || ''}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Specialization"
                  fullWidth
                  value={editForm.specialization || ''}
                  onChange={(e) => setEditForm({ ...editForm, specialization: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Experience (years)"
                  type="number"
                  fullWidth
                  value={editForm.experience_years || ''}
                  onChange={(e) => setEditForm({ ...editForm, experience_years: parseInt(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Expertise (comma-separated)"
                  fullWidth
                  placeholder="Vedic, KP, Nadi, Marriage, Career"
                  value={editForm.expertise || ''}
                  onChange={(e) => setEditForm({ ...editForm, expertise: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Languages (comma-separated)"
                  fullWidth
                  placeholder="Tamil, Hindi, English"
                  value={editForm.languages || ''}
                  onChange={(e) => setEditForm({ ...editForm, languages: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rates Dialog */}
        <Dialog open={ratesOpen} onClose={() => setRatesOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Set Rates — {ratesAstro?.name || ratesAstro?.user?.name || ''}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  label="Chat Rate (₹/min)"
                  type="number"
                  fullWidth
                  value={ratesForm.chat_rate_per_min || ''}
                  onChange={(e) => setRatesForm({ ...ratesForm, chat_rate_per_min: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Voice Rate (₹/min)"
                  type="number"
                  fullWidth
                  value={ratesForm.voice_rate_per_min || ''}
                  onChange={(e) => setRatesForm({ ...ratesForm, voice_rate_per_min: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Video Rate (₹/min)"
                  type="number"
                  fullWidth
                  value={ratesForm.video_rate_per_min || ''}
                  onChange={(e) => setRatesForm({ ...ratesForm, video_rate_per_min: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRatesOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveRates} disabled={savingRates}>
              {savingRates ? 'Saving...' : 'Save Rates'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>
    </AdminLayout>
  );
}
